import {
  csmAccountCountTitles,
  csmBusinessValueTitles,
  csmQuotaMetricTitles,
  csmQuotaTypeTitles,
  csmRenewalValueTitles,
  jobsInquiryRoleNames,
  vpDepartmentSizes,
  vpManagedRoles,
  vpQuotaTypes,
} from './optionsDicts';
import type { SelectableOption } from '@/components/partials/jobs/onboarding/types';
import {
  CompanySeriesStageSlug,
  JobsInquiryAeQuota,
  JobsInquiryCsmAccountNumber,
  JobsInquiryCsmBusinessValue,
  JobsInquiryCsmQuota,
  JobsInquiryCsmQuotaMetric,
  JobsInquiryCsmQuotaPeriod,
  JobsInquiryCsmRenewalValue,
  JobsInquiryDealBreaker,
  JobsInquiryDealSize,
  JobsInquiryEmployment,
  JobsInquiryPosition,
  JobsInquirySalesCycleLength,
  JobsInquirySalesMethodology,
  JobsInquirySdrQuota,
  JobsInquiryVpDepartmentSize,
  JobsInquiryVpManagedRole,
  JobsInquiryVpQuota,
  JobsInquiryAeLeadSource,
} from '@/types/graphql';

export const jobsAeSelectableQuotaPeriods: SelectableOption[] = [
  {
    title: 'Monthly',
    key: JobsInquiryAeQuota.Monthly,
    returnValue: JobsInquiryAeQuota.Monthly,
  },
  {
    title: 'Quarterly',
    key: JobsInquiryAeQuota.Quarterly,
    returnValue: JobsInquiryAeQuota.Quarterly,
  },
  {
    title: 'Annually',
    key: JobsInquiryAeQuota.Annual,
    returnValue: JobsInquiryAeQuota.Annual,
  },
];

export const jobsCsmSelectableQuotaPeriods: SelectableOption[] = [
  {
    title: 'Monthly',
    key: JobsInquiryCsmQuotaPeriod.Monthly,
    returnValue: JobsInquiryCsmQuotaPeriod.Monthly,
  },
  {
    title: 'Quarterly',
    key: JobsInquiryCsmQuotaPeriod.Quarterly,
    returnValue: JobsInquiryCsmQuotaPeriod.Quarterly,
  },
  {
    title: 'Annually',
    key: JobsInquiryCsmQuotaPeriod.Annual,
    returnValue: JobsInquiryCsmQuotaPeriod.Annual,
  },
];

export const jobsDealBrakers: SelectableOption[] = [
  {
    title: 'I don’t want to work in an office',
    key: JobsInquiryDealBreaker.OfficeWork,
  },
  {
    title: 'I don’t want to work for a fully-remote company',
    key: JobsInquiryDealBreaker.RemoteCompany,
  },
  {
    title: 'I don’t want to work somewhere without SDRs',
    subtitle: 'Must self-prospect all deals',
    key: JobsInquiryDealBreaker.WithoutSdr,
  },
  {
    title: 'I don’t want to sell a brand-new product or into an unproven vertical',
    key: JobsInquiryDealBreaker.NewProduct,
  },
  {
    title: 'None',
    subtitle: 'I’m willing to work at any company where I can make a lot of money and the product is really good.',
    key: JobsInquiryDealBreaker.None,
  },
];

export const dealBreakersWithoutSdrOption = jobsDealBrakers.filter(
  option => option.key !== JobsInquiryDealBreaker.WithoutSdr,
);

export const employmentTypes: SelectableOption[] = [
  {
    title: 'Full-time',
    subtitle: 'Regular jobs that provide your main source of income',
    key: JobsInquiryEmployment.FullTime,
  },
  {
    title: 'Side-hustle',
    subtitle: 'Jobs you can take to supplement your income',
    key: JobsInquiryEmployment.SideHustle,
  },
];

export const companyStages: SelectableOption[] = [
  {
    title: 'Startup',
    subtitle: 'Seed or Series A company',
    key: CompanySeriesStageSlug.Startup,
  },
  {
    title: 'Growth',
    subtitle: 'Series B – D company',
    key: CompanySeriesStageSlug.Growth,
  },
  {
    title: 'Large',
    subtitle: 'Series E – G company',
    key: CompanySeriesStageSlug.Large,
  },
  {
    title: 'Massive',
    subtitle: 'An IPO company',
    key: CompanySeriesStageSlug.Massive,
  },
];

export const jobsDealSizes: SelectableOption[] = [
  {
    title: 'Less than $10,000 / year',
    key: JobsInquiryDealSize.DealLess_10000,
  },
  {
    title: '$10,000 – $50,000 / year',
    key: JobsInquiryDealSize.Deal_10000_50000,
  },
  {
    title: '$50,000 – $100,000 / year',
    key: JobsInquiryDealSize.Deal_50000_100000,
  },
  {
    title: '$100,000 – $500,000 / year',
    key: JobsInquiryDealSize.Deal_100000_500000,
  },
  {
    title: 'More than $500,000 / year',
    key: JobsInquiryDealSize.DealMore_500000,
  },
];

export const jobsSalesCycles: SelectableOption[] = [
  {
    title: 'Less than 1 week',
    key: JobsInquirySalesCycleLength.ValueLess_1Week,
  },
  {
    title: '1 week to 1 month',
    key: JobsInquirySalesCycleLength.Value_1Week_1Month,
  },
  {
    title: '1 month to 3 months',
    key: JobsInquirySalesCycleLength.Value_1Month_3Months,
  },
  {
    title: 'More than 3 months',
    key: JobsInquirySalesCycleLength.Value_3Months_1Year,
  },
  {
    title: 'Longer than 1 year',
    key: JobsInquirySalesCycleLength.ValueLonger_1Year,
  },
];

export const jobsSDRQuotaTypes: SelectableOption[] = [
  {
    title: '# of appointments set',
    key: JobsInquirySdrQuota.Appointment,
  },
  {
    title: '# of qualified opportunities',
    key: JobsInquirySdrQuota.Opportunity,
  },
  {
    title: '$ of pipeline',
    key: JobsInquirySdrQuota.Pipeline,
  },
  {
    title: '$ of revenue',
    key: JobsInquirySdrQuota.Revenue,
  },
];

export const jobsLeadSources: SelectableOption[] = [
  {
    title: 'SDR sets appointments',
    key: JobsInquiryAeLeadSource.Sdr,
  },
  {
    title: 'Inbound from Marketing',
    key: JobsInquiryAeLeadSource.Marketing,
  },
  {
    title: 'Self-prospected',
    subtitle: 'I am cold calling or emailing',
    key: JobsInquiryAeLeadSource.Self,
  },
  {
    title: 'Current customer upsells',
    key: JobsInquiryAeLeadSource.Customer,
  },
];

export const salesMethodologies: SelectableOption[] = [
  { title: 'Sandler', key: JobsInquirySalesMethodology.Sandler },
  { title: 'Challenger', key: JobsInquirySalesMethodology.Challenger },
  { title: 'MEDDIC', key: JobsInquirySalesMethodology.Meddic },
  { title: 'Spin Selling', key: JobsInquirySalesMethodology.SpinSelling },
  { title: 'SNAP Selling', key: JobsInquirySalesMethodology.SnapSelling },
  { title: 'Conceptual Selling (Heiman-Miller)', key: JobsInquirySalesMethodology.ConceptualSelling },
  { title: 'No formal training', key: JobsInquirySalesMethodology.NoFormalTraining },
];

export const csmBusinessValue: SelectableOption[] = [
  {
    title: csmBusinessValueTitles[JobsInquiryCsmBusinessValue.ValueLess_250KUsd],
    key: JobsInquiryCsmBusinessValue.ValueLess_250KUsd,
    returnValue: JobsInquiryCsmBusinessValue.ValueLess_250KUsd,
  },
  {
    title: csmBusinessValueTitles[JobsInquiryCsmBusinessValue.Value_251K_1MUsd],
    key: JobsInquiryCsmBusinessValue.Value_251K_1MUsd,
    returnValue: JobsInquiryCsmBusinessValue.Value_251K_1MUsd,
  },
  {
    title: csmBusinessValueTitles[JobsInquiryCsmBusinessValue.Value_1_1M_2MUsd],
    key: JobsInquiryCsmBusinessValue.Value_1_1M_2MUsd,
    returnValue: JobsInquiryCsmBusinessValue.Value_1_1M_2MUsd,
  },
  {
    title: csmBusinessValueTitles[JobsInquiryCsmBusinessValue.Value_2_1M_5MUsd],
    key: JobsInquiryCsmBusinessValue.Value_2_1M_5MUsd,
    returnValue: JobsInquiryCsmBusinessValue.Value_2_1M_5MUsd,
  },
  {
    title: csmBusinessValueTitles[JobsInquiryCsmBusinessValue.ValueMore_5MUsd],
    key: JobsInquiryCsmBusinessValue.ValueMore_5MUsd,
    returnValue: JobsInquiryCsmBusinessValue.ValueMore_5MUsd,
  },
];

export const csmAccountCount: SelectableOption[] = [
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.Number_1_10],
    key: JobsInquiryCsmAccountNumber.Number_1_10,
    returnValue: JobsInquiryCsmAccountNumber.Number_1_10,
  },
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.Number_11_50],
    key: JobsInquiryCsmAccountNumber.Number_11_50,
    returnValue: JobsInquiryCsmAccountNumber.Number_11_50,
  },
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.Number_51_100],
    key: JobsInquiryCsmAccountNumber.Number_51_100,
    returnValue: JobsInquiryCsmAccountNumber.Number_51_100,
  },
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.Number_101_250],
    key: JobsInquiryCsmAccountNumber.Number_101_250,
    returnValue: JobsInquiryCsmAccountNumber.Number_101_250,
  },
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.Number_251_500],
    key: JobsInquiryCsmAccountNumber.Number_251_500,
    returnValue: JobsInquiryCsmAccountNumber.Number_251_500,
  },
  {
    title: csmAccountCountTitles[JobsInquiryCsmAccountNumber.NumberMore_500],
    key: JobsInquiryCsmAccountNumber.NumberMore_500,
    returnValue: JobsInquiryCsmAccountNumber.NumberMore_500,
  },
];

export const csmQuotaTypes: SelectableOption[] = [
  {
    title: csmQuotaTypeTitles[JobsInquiryCsmQuota.CsmTeamQuota],
    key: JobsInquiryCsmQuota.CsmTeamQuota,
    returnValue: JobsInquiryCsmQuota.CsmTeamQuota,
  },
  {
    title: csmQuotaTypeTitles[JobsInquiryCsmQuota.CsmIndividualQuota],
    key: JobsInquiryCsmQuota.CsmIndividualQuota,
    returnValue: JobsInquiryCsmQuota.CsmIndividualQuota,
  },
  {
    title: csmQuotaTypeTitles[JobsInquiryCsmQuota.CsmNoQuota],
    key: JobsInquiryCsmQuota.CsmNoQuota,
    returnValue: JobsInquiryCsmQuota.CsmNoQuota,
  },
];

export const csmQuotaMetrics: SelectableOption[] = [
  {
    title: csmQuotaMetricTitles[JobsInquiryCsmQuotaMetric.CsmRenewals],
    key: JobsInquiryCsmQuotaMetric.CsmRenewals,
    returnValue: JobsInquiryCsmQuotaMetric.CsmRenewals,
  },
  {
    title: csmQuotaMetricTitles[JobsInquiryCsmQuotaMetric.CsmUpsells],
    key: JobsInquiryCsmQuotaMetric.CsmUpsells,
    returnValue: JobsInquiryCsmQuotaMetric.CsmUpsells,
  },
  {
    title: csmQuotaMetricTitles[JobsInquiryCsmQuotaMetric.CsmOpportunities],
    key: JobsInquiryCsmQuotaMetric.CsmOpportunities,
    returnValue: JobsInquiryCsmQuotaMetric.CsmOpportunities,
  },
  {
    title: csmQuotaMetricTitles[JobsInquiryCsmQuotaMetric.CsmEngagement],
    key: JobsInquiryCsmQuotaMetric.CsmEngagement,
    returnValue: JobsInquiryCsmQuotaMetric.CsmEngagement,
  },
  {
    title: csmQuotaMetricTitles[JobsInquiryCsmQuotaMetric.CsmCustomerSatisfaction],
    key: JobsInquiryCsmQuotaMetric.CsmCustomerSatisfaction,
    returnValue: JobsInquiryCsmQuotaMetric.CsmCustomerSatisfaction,
  },
];

export const csmRenewalValues: SelectableOption[] = [
  {
    title: csmRenewalValueTitles[JobsInquiryCsmRenewalValue.ValueLess_1KUsd],
    key: JobsInquiryCsmRenewalValue.ValueLess_1KUsd,
    returnValue: JobsInquiryCsmRenewalValue.ValueLess_1KUsd,
  },
  {
    title: csmRenewalValueTitles[JobsInquiryCsmRenewalValue.Value_1K_5KUsd],
    key: JobsInquiryCsmRenewalValue.Value_1K_5KUsd,
    returnValue: JobsInquiryCsmRenewalValue.Value_1K_5KUsd,
  },
  {
    title: csmRenewalValueTitles[JobsInquiryCsmRenewalValue.Value_5K_25KUsd],
    key: JobsInquiryCsmRenewalValue.Value_5K_25KUsd,
    returnValue: JobsInquiryCsmRenewalValue.Value_5K_25KUsd,
  },
  {
    title: csmRenewalValueTitles[JobsInquiryCsmRenewalValue.Value_25K_100KUsd],
    key: JobsInquiryCsmRenewalValue.Value_25K_100KUsd,
    returnValue: JobsInquiryCsmRenewalValue.Value_25K_100KUsd,
  },
  {
    title: csmRenewalValueTitles[JobsInquiryCsmRenewalValue.ValueMore_100KUsd],
    key: JobsInquiryCsmRenewalValue.ValueMore_100KUsd,
    returnValue: JobsInquiryCsmRenewalValue.ValueMore_100KUsd,
  },
];

export const salesRoleNames: SelectableOption[] = [
  {
    title: jobsInquiryRoleNames[JobsInquiryPosition.AccountExecutive],
    subtitle: 'I want to close deals',
    key: JobsInquiryPosition.AccountExecutive,
    returnValue: JobsInquiryPosition.AccountExecutive,
  },
  {
    title: jobsInquiryRoleNames[JobsInquiryPosition.Vp],
    subtitle: 'I want to manage sales reps and revenue',
    key: JobsInquiryPosition.Vp,
    returnValue: JobsInquiryPosition.Vp,
  },
  {
    title: jobsInquiryRoleNames[JobsInquiryPosition.Sdr],
    subtitle: 'I want to manage the discovery phase',
    key: JobsInquiryPosition.Sdr,
    returnValue: JobsInquiryPosition.Sdr,
  },
  {
    title: jobsInquiryRoleNames[JobsInquiryPosition.Csm],
    subtitle: 'I want to manage existing accounts, up-sells, and renewals',
    key: JobsInquiryPosition.Csm,
    returnValue: JobsInquiryPosition.Csm,
  },
];

export const jobsVpDepartmentSizes: SelectableOption[] = [
  {
    title: vpDepartmentSizes[JobsInquiryVpDepartmentSize.VpDepartmentSize_1_20],
    key: JobsInquiryVpDepartmentSize.VpDepartmentSize_1_20,
  },
  {
    title: vpDepartmentSizes[JobsInquiryVpDepartmentSize.VpDepartmentSize_21_50],
    key: JobsInquiryVpDepartmentSize.VpDepartmentSize_21_50,
  },
  {
    title: vpDepartmentSizes[JobsInquiryVpDepartmentSize.VpDepartmentSize_51_75],
    key: JobsInquiryVpDepartmentSize.VpDepartmentSize_51_75,
  },
  {
    title: vpDepartmentSizes[JobsInquiryVpDepartmentSize.VpDepartmentSize_76_100],
    key: JobsInquiryVpDepartmentSize.VpDepartmentSize_76_100,
  },
  {
    title: vpDepartmentSizes[JobsInquiryVpDepartmentSize.VpDepartmentSizeMore_100],
    key: JobsInquiryVpDepartmentSize.VpDepartmentSizeMore_100,
  },
];

export const jobsVpQuotaTypes: SelectableOption[] = [
  {
    title: vpQuotaTypes[JobsInquiryVpQuota.VpQuotaIndividual],
    key: JobsInquiryVpQuota.VpQuotaIndividual,
  },
  {
    title: vpQuotaTypes[JobsInquiryVpQuota.VpQuotaTeam],
    key: JobsInquiryVpQuota.VpQuotaTeam,
  },
];

export const jobsVpManagedRoles: SelectableOption[] = [
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleSdr],
    key: JobsInquiryVpManagedRole.VpManagedRoleSdr,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleAe],
    key: JobsInquiryVpManagedRole.VpManagedRoleAe,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleCsm],
    key: JobsInquiryVpManagedRole.VpManagedRoleCsm,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleSe],
    key: JobsInquiryVpManagedRole.VpManagedRoleSe,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleSalesOps],
    key: JobsInquiryVpManagedRole.VpManagedRoleSalesOps,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleSalesEnablement],
    key: JobsInquiryVpManagedRole.VpManagedRoleSalesEnablement,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleSdrManager],
    key: JobsInquiryVpManagedRole.VpManagedRoleSdrManager,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleAeManager],
    key: JobsInquiryVpManagedRole.VpManagedRoleAeManager,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleDirector],
    key: JobsInquiryVpManagedRole.VpManagedRoleDirector,
  },
  {
    title: vpManagedRoles[JobsInquiryVpManagedRole.VpManagedRoleOther],
    key: JobsInquiryVpManagedRole.VpManagedRoleOther,
  },
];
